import { enGB } from 'date-fns/locale'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { registerLocale } from 'react-datepicker'
import api from '../../../helpers/apiRequester'
import {
    Button,
    Column,
    DataTable,
    DatePicker,
    Field,
    Pagination,
    SearchInput,
    SegmentedControl,
    StatusPill,
    Toolbar,
    useToast,
} from '../../../ui'
import styles from './styles.module.scss'

registerLocale('en-GB', enGB)

const PAGE_SIZE_DEFAULT = 25

/** Kit DatePicker is typed as a single date; this page needs a range input. */
const CreatedRangePicker = DatePicker as unknown as (props: Record<string, unknown>) => JSX.Element

type SignedFilter = 'all' | 'unsigned' | 'signed'

interface LoaTrackerRow {
    id: number
    case_ref: string | null
    signed: number | string | boolean | null
    lender: string | null
    signature_date: string | null
    created_at: string | null
    first_name: string | null
    last_name: string | null
    email: string | null
    phone: string | null
    proclaim_reference: string | null
    num_chases: number | string | null
    chase_date: string | null
}

const SIGNED_OPTIONS: { value: SignedFilter; label: string }[] = [
    { value: 'unsigned', label: 'Unsigned' },
    { value: 'signed', label: 'Signed' },
    { value: 'all', label: 'All' },
]

const signedPayload = (filter: SignedFilter): 0 | 1 | 'all' => {
    if (filter === 'unsigned') return 0
    if (filter === 'signed') return 1
    return 'all'
}

const isSigned = (value: LoaTrackerRow['signed']): boolean => value === 1 || value === '1' || value === true

const displayText = (value: string | null | undefined): string => {
    if (value == null) return ''
    return String(value)
}

const clientName = (row: LoaTrackerRow): string =>
    [row.first_name, row.last_name].map((part) => (part == null ? '' : String(part).trim())).filter(Boolean).join(' ')

const toIsoDate = (date: Date): string => {
    const month = `${date.getMonth() + 1}`.padStart(2, '0')
    const day = `${date.getDate()}`.padStart(2, '0')
    return `${date.getFullYear()}-${month}-${day}`
}

const fromIsoDate = (value: string): Date | null => {
    if (!value) return null
    const parsed = new Date(`${value}T00:00:00`)
    return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatDate = (value: string | null): string => {
    if (!value) return ''
    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })
    }
    const dateOnly = value.includes('T') ? value.split('T')[0] : value.split(' ')[0]
    return dateOnly || value
}

const errorMessage = (error: unknown, fallback: string): string => {
    const err = error as { response?: { data?: { message?: string } }; message?: string }
    return err?.response?.data?.message || err?.message || fallback
}

export const LoaTrackerView = () => {
    const { toast } = useToast()
    const [signed, setSigned] = useState<SignedFilter>('unsigned')
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [createdFrom, setCreatedFrom] = useState('')
    const [createdTo, setCreatedTo] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT)
    const [rows, setRows] = useState<LoaTrackerRow[]>([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [resendingId, setResendingId] = useState<number | null>(null)

    const fetchList = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await api.post('loa-tracker', {
                signed: signedPayload(signed),
                search: debouncedSearch || undefined,
                createdDateFrom: createdFrom || undefined,
                createdDateTo: createdTo || undefined,
                page,
                pageSize,
            })
            if (!response.data?.success) {
                setRows([])
                setTotal(0)
                setError(response.data?.message || 'Could not load LOA links.')
                return
            }
            const nextTotal = Number(response.data.total) || 0
            const lastPage = Math.max(1, Math.ceil(nextTotal / pageSize))
            if (page > lastPage) {
                setPage(lastPage)
                return
            }
            setRows(Array.isArray(response.data.rows) ? response.data.rows : [])
            setTotal(nextTotal)
        } catch (err) {
            setRows([])
            setTotal(0)
            setError(errorMessage(err, 'Could not load LOA links.'))
        } finally {
            setLoading(false)
        }
    }, [signed, debouncedSearch, createdFrom, createdTo, page, pageSize])

    useEffect(() => {
        void fetchList()
    }, [fetchList])

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim())
            setPage(1)
        }, 300)
        return () => clearTimeout(timer)
    }, [search])

    const patchSigned = (value: SignedFilter) => {
        setSigned(value)
        setPage(1)
    }

    const changePageSize = (size: number) => {
        setPageSize(size)
        setPage(1)
    }

    const resend = useCallback(
        (row: LoaTrackerRow) => {
            Confirm.show(
                'Resend LOA',
                'This sends the same Letter of Authority request email as the original send, using the existing portal link.',
                'Resend',
                'Cancel',
                () => {
                    void (async () => {
                        setResendingId(row.id)
                        try {
                            const response = await api.post('loa-tracker/resend', { id: row.id })
                            if (!response.data?.success) {
                                toast({
                                    title: response.data?.message || 'Failed to resend LOA',
                                    tone: 'bad',
                                })
                                return
                            }
                            setRows((current) =>
                                current.map((item) =>
                                    item.id === row.id
                                        ? {
                                              ...item,
                                              num_chases: response.data.num_chases,
                                              chase_date: response.data.chase_date,
                                          }
                                        : item
                                )
                            )
                            toast({ title: 'LOA resent', tone: 'good' })
                        } catch (err) {
                            toast({ title: errorMessage(err, 'Failed to resend LOA'), tone: 'bad' })
                        } finally {
                            setResendingId(null)
                        }
                    })()
                }
            )
        },
        [toast]
    )

    const columns: Column<LoaTrackerRow>[] = useMemo(
        () => [
            {
                key: 'proclaim_reference',
                header: 'Proclaim',
                nowrap: true,
                render: (row) => displayText(row.proclaim_reference),
            },
            {
                key: 'case_ref',
                header: 'Case',
                nowrap: true,
                render: (row) => displayText(row.case_ref),
            },
            {
                key: 'client',
                header: 'Client',
                render: (row) => {
                    const email = displayText(row.email)
                    const phone = displayText(row.phone)
                    return (
                        <div className={styles.client}>
                            <span className={styles.clientName}>{clientName(row)}</span>
                            {email ? <span className={styles.clientEmail}>{email}</span> : null}
                            {phone ? <span className={styles.clientEmail}>{phone}</span> : null}
                        </div>
                    )
                },
            },
            {
                key: 'lender',
                header: 'Lender',
                render: (row) => displayText(row.lender),
            },
            {
                key: 'signed',
                header: 'Signed',
                nowrap: true,
                render: (row) =>
                    isSigned(row.signed) ? (
                        <StatusPill tone="good" dot size="sm">
                            Signed
                        </StatusPill>
                    ) : (
                        <StatusPill tone="neutral" dot size="sm">
                            Unsigned
                        </StatusPill>
                    ),
            },
            {
                key: 'signature_date',
                header: 'Signed on',
                nowrap: true,
                render: (row) => formatDate(row.signature_date),
            },
            {
                key: 'created_at',
                header: 'Created',
                nowrap: true,
                render: (row) => formatDate(row.created_at),
            },
            {
                key: 'num_chases',
                header: 'Chases',
                nowrap: true,
                render: (row) => (row.num_chases == null ? '' : String(row.num_chases)),
            },
            {
                key: 'chase_date',
                header: 'Last chase',
                nowrap: true,
                render: (row) => formatDate(row.chase_date),
            },
            {
                key: 'actions',
                header: '',
                align: 'right',
                nowrap: true,
                render: (row) => (
                    <Button
                        size="sm"
                        variant="neutral"
                        loading={resendingId === row.id}
                        disabled={resendingId !== null}
                        onClick={(event) => {
                            event.stopPropagation()
                            resend(row)
                        }}
                    >
                        Resend
                    </Button>
                ),
            },
        ],
        [resend, resendingId]
    )

    return (
        <>
            <Toolbar>
                <Field label="Search">
                    <SearchInput
                        className={styles.search}
                        value={search}
                        onChange={setSearch}
                        placeholder="Proclaim, case, client or lender"
                        aria-label="Search LOA links"
                    />
                </Field>
                <Field label="Signed status">
                    <SegmentedControl
                        aria-label="Signed status"
                        options={SIGNED_OPTIONS}
                        value={signed}
                        onChange={patchSigned}
                    />
                </Field>
                <Field label="Created">
                    <CreatedRangePicker
                        className={styles.dateRange}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Any date"
                        locale={enGB}
                        isClearable
                        portalId="root"
                        selectsRange
                        startDate={fromIsoDate(createdFrom)}
                        endDate={fromIsoDate(createdTo)}
                        onChange={([start, end]: [Date | null, Date | null]) => {
                            setCreatedFrom(start ? toIsoDate(start) : '')
                            setCreatedTo(end ? toIsoDate(end) : '')
                            setPage(1)
                        }}
                    />
                </Field>
            </Toolbar>
            <DataTable
                columns={columns}
                rows={rows}
                rowKey={(row) => row.id}
                loading={loading}
                error={error}
                emptyTitle="No LOA links match these filters"
                emptyDescription="Try another signed status or a wider created-date range."
                footer={
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        total={total}
                        onChange={setPage}
                        onPageSizeChange={changePageSize}
                    />
                }
            />
        </>
    )
}
